import { BaseApiClient } from './base-client';

export interface SharePermissions {
  read: boolean;
  write: boolean;
  execute: boolean;
  admin: boolean;
}

export interface SambaShare {
  name: string;
  path: string;
  description?: string;
  guest_ok: boolean;
  browseable: boolean;
  permissions: SharePermissions;
  status: string;
}

export interface SambaShareCreate {
  name: string;
  path: string;
  description?: string;
  guest_ok?: boolean;
  browseable?: boolean;
  permissions: SharePermissions;
}

export interface SambaShareUpdate {
  description?: string;
  guest_ok?: boolean;
  browseable?: boolean;
  permissions?: SharePermissions;
}

export interface UserPermission {
  username: string;
  permissions: SharePermissions;
}

export interface SambaStatus {
  status: string;
  status_smbd: string;
  status_nmbd: string;
  active_shares: number;
  connected_users: number;
  server_string: string;
  workgroup: string;
  security: string;
  uptime: string;
}

export class SambaApiClient extends BaseApiClient {
  // Share management
  async listShares(serviceId: number): Promise<SambaShare[]> {
    return this.get<SambaShare[]>(`/api/v1/samba/${serviceId}/shares`);
  }

  async createShare(serviceId: number, share: SambaShareCreate): Promise<SambaShare> {
    return this.post<SambaShare>(`/api/v1/samba/${serviceId}/shares`, share);
  }

  async updateShare(serviceId: number, shareName: string, updates: SambaShareUpdate): Promise<SambaShare> {
    return this.patch<SambaShare>(`/api/v1/samba/${serviceId}/shares/${shareName}`, updates);
  }

  async deleteShare(serviceId: number, shareName: string): Promise<{ status: string }> {
    return this.delete<{ status: string }>(`/api/v1/samba/${serviceId}/shares/${shareName}`);
  }

  // User permissions
  async listShareUsers(serviceId: number, shareName: string): Promise<UserPermission[]> {
    return this.get<UserPermission[]>(`/api/v1/samba/${serviceId}/shares/${shareName}/users`);
  }

  async addShareUser(serviceId: number, shareName: string, userPermission: UserPermission): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/samba/${serviceId}/shares/${shareName}/users`, userPermission);
  }

  async removeShareUser(serviceId: number, shareName: string, username: string): Promise<{ status: string }> {
    return this.delete<{ status: string }>(`/api/v1/samba/${serviceId}/shares/${shareName}/users/${username}`);
  }

  // Permissions management
  async getSharePermissions(serviceId: number, shareName: string): Promise<any> {
    return this.get<any>(`/api/v1/samba/${serviceId}/shares/${shareName}/permissions`);
  }

  async updateUserPermissions(serviceId: number, shareName: string, username: string, permissions: { [key: string]: boolean }): Promise<{ status: string }> {
    return this.patch<{ status: string }>(`/api/v1/samba/${serviceId}/shares/${shareName}/permissions/${username}`, permissions);
  }

  // Service control
  async getStatus(serviceId: number): Promise<SambaStatus> {
    return this.get<SambaStatus>(`/api/v1/samba/${serviceId}/status`);
  }

  async reloadService(serviceId: number): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/samba/${serviceId}/reload`);
  }

  // Settings
  async updateSettings(serviceId: number, settings: { bind_to_public?: boolean }): Promise<{ status: string }> {
    return this.post<{ status: string }>(`/api/v1/samba/${serviceId}/settings`, settings);
  }
}